/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { QrCode, Store, CreditCard, Megaphone, Car, ExternalLink } from "lucide-react";

const supports = [
  { icon: Store, label: "Magasin" },
  { icon: Store, label: "Comptoir" },
  { icon: CreditCard, label: "Carte de visite" },
  { icon: Megaphone, label: "Flyer" },
  { icon: Car, label: "Véhicule" },
];

const benefits = [
  "Plus de visites sur votre site",
  "Plus d'avis Google",
  "Plus de demandes de rendez-vous",
];

export const QRCodeSection = () => (
  <section id="qr-code" data-testid="qrcode-section" className="py-24 md:py-32 bg-gradient-to-br from-ink via-ink to-ink-700 text-white relative overflow-hidden">
    <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-cyan/15 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3" aria-hidden />
    <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-cyan/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" aria-hidden />

    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="font-mono-label text-cyan-400 flex items-center">
            <span className="rule bg-cyan" /> Service QR Code professionnel
          </p>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Facilitez <em className="italic font-light">l'accès à votre site</em>.
          </h2>
          <p className="mt-8 text-white/80 leading-relaxed text-lg">
            Une <strong className="text-white">page dédiée</strong> et un{" "}
            <strong className="text-white">QR Code personnalisé</strong> à afficher partout où vos clients
            vous rencontrent.
          </p>

          <div className="mt-10">
            <p className="font-mono-label text-white/55">À afficher sur</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {supports.map((s) => {
                const Icon = s.icon;
                return (
                  <span
                    key={s.label}
                    className="inline-flex items-center gap-2 px-4 h-10 bg-white/5 border border-white/15 text-sm hover:bg-white/10 transition-colors"
                  >
                    <Icon size={14} className="text-cyan-400" />
                    {s.label}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="mt-10">
            <p className="font-mono-label text-white/55">Bénéfices concrets</p>
            <ul className="mt-4 space-y-2.5">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-3 text-white/90">
                  <span className="h-7 w-7 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 inline-flex items-center justify-center text-white text-xs font-bold shrink-0">✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <a
            href="#contact"
            data-testid="qrcode-cta"
            className="mt-10 inline-flex items-center gap-3 h-14 px-7 bg-white text-ink text-sm font-medium hover:bg-cyan-400 hover:text-white transition-colors group"
          >
            Créer mon QR Code
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </a>
        </div>

        {/* Mock QR card */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white text-ink p-10 relative"
          >
            <div className="text-center">
              <p className="font-mono-label text-cyan-600">Avis clients Google</p>
              <p className="mt-2 font-display text-2xl text-ink">Scannez ce QR Code</p>
              <p className="mt-2 text-sm text-ink/60 leading-relaxed">
                pour consulter les avis de nos clients sur Google.
              </p>
            </div>

            <div className="mt-8 mx-auto w-56 h-56 relative flex items-center justify-center">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_webup-agency/artifacts/nyaw54i3_image.png"
                alt="QR Code redirigeant vers les avis clients WebUP sur Google"
                title="Avis clients WebUP — Google"
                width="224"
                height="224"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain"
              />
            </div>

            <p className="mt-6 text-center text-xs text-ink/55">google.com — Avis WebUP</p>

            <div className="mt-6 pt-6 border-t border-ink/10 text-center">
              <p className="font-display text-3xl text-ink">+82 %</p>
              <p className="font-mono-label text-ink/55 mt-1">de visites en moyenne</p>
              <p className="mt-3 text-[10px] text-ink/45 flex items-center justify-center gap-1.5">
                <ExternalLink size={10} /> Source : Statista — usage QR codes France 2024.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
